import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { TpDataContext } from '../interfaces';
import { TpAssetInfo } from './interfaces';
import { getAssetInfo } from './helpers';

export interface TpQueriedAssetsData {
  searchText: string;
  typeFilter: string;
  sortBy: string;
  sortAsc: boolean;
  page: number; // zero-based page index
  fetchedAssets: TpAssetInfo[]; // this will be used for the assets we fetch based on filters and sorting
  queriedAssets: TpAssetInfo[]; // this will be the state that will be populated by related assets that are not on the startpage
  openedAssets: TpAssetInfo[];
  queriedAssetTypes: any[];
  queryStats: any;
  catalogAsset: TpAssetInfo | null; // cached catalog asset
  lastSearchQuery: Record<string, string>;
}

const initialState: TpQueriedAssetsData = {
  searchText: '',
  typeFilter: '',
  sortBy: '',
  sortAsc: true,
  page: 0,
  fetchedAssets: [],
  queriedAssets: [],
  openedAssets: [],
  queriedAssetTypes: [],
  queryStats: {},
  catalogAsset: null,
  lastSearchQuery: {},
};

updateQueriedAssets(initialState);

const openedAssetsStr = window.sessionStorage.getItem('openedAssets');
if (openedAssetsStr) {
  initialState.openedAssets = JSON.parse(openedAssetsStr);
  initialState.queriedAssets = JSON.parse(openedAssetsStr);
}

const lastSearchQueryStr = window.sessionStorage.getItem('lastSearchQuery');
if (lastSearchQueryStr) {
  initialState.lastSearchQuery = JSON.parse(lastSearchQueryStr);
}

function updateQueriedAssets(state: TpQueriedAssetsData) {
  let fetchedAssets = state.fetchedAssets;

  const assetTypesMap: { [id: string]: any } = {};
  const assetTypes = [];
  for (const asset of fetchedAssets) {
    if (!assetTypesMap[asset.type]) {
      const assetTypeInfo = {
        type: asset.type,
        count: 0,
      };
      assetTypesMap[asset.type] = assetTypeInfo;
      assetTypes.push(assetTypeInfo);
    }
    assetTypesMap[asset.type].count++;
  }
  state.queriedAssetTypes = assetTypes;

  if (state.typeFilter) fetchedAssets = fetchedAssets.filter((asset) => asset.type == state.typeFilter);
  state.fetchedAssets = fetchedAssets;
}

export function findOpenedAsset(state: TpQueriedAssetsData, assetId: string): TpAssetInfo | null {
  for (const asset of state.openedAssets) if (asset.id == assetId) return asset;
  return null;
}

function saveOpenedAssets(state: TpQueriedAssetsData) {
  window.sessionStorage.setItem('openedAssets', JSON.stringify(state.openedAssets));
}

function clearOpenedAssets() {
  window.sessionStorage.setItem('openedAssets', JSON.stringify([]));
}

function saveLastSearchQuery(state: TpQueriedAssetsData) {
  window.sessionStorage.setItem('lastSearchQuery', JSON.stringify(state.lastSearchQuery));
}

function clearLastSearchQuery() {
  window.sessionStorage.setItem('lastSearchQuery', JSON.stringify({}));
}

function performCreateAndOpenListAsset(
  state: TpQueriedAssetsData,
  newAssetId: string,
  parentAssetId: string,
  dataContext: TpDataContext,
  canonicalTypeId: string,
  name: string,
  description: string,
) {
  let type = '';
  // @todo: this must become generic!
  if (canonicalTypeId == 'species') type = 'species_list';
  if (canonicalTypeId == 'publications') type = 'publication_list';
  const asset = {
    id: newAssetId,
    type,
    name,
    description,
    creationDate: new Date().toLocaleString(),
    lastModifiedDate: new Date().toLocaleString(),
    relations: [{ type: 'list_produced_by', to: parentAssetId }],
  } as TpAssetInfo;
  addNewAsset({ asset });
}

export const queriedAssetsSlice = createSlice({
  name: 'queriedAssets',
  initialState,
  reducers: {
    setSearchText: (state, action: PayloadAction<{ searchText: string }>) => {
      state.searchText = action.payload.searchText;
      state.page = 0; // reset to first page on search change
      updateQueriedAssets(state);
    },
    setAssetLastSearchQuery: (state, action: PayloadAction<{ assetId: string; query: string }>) => {
      state.lastSearchQuery[action.payload.assetId] = action.payload.query;
      saveLastSearchQuery(state);
    },
    setTypeFilter: (state, action: PayloadAction<{ typeFilter: string }>) => {
      state.typeFilter = action.payload.typeFilter;
      state.page = 0; // reset to first page on filter change
      updateQueriedAssets(state);
    },
    setSortBy: (state, action: PayloadAction<{ sortBy: string; asc: boolean }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortAsc = action.payload.asc;
      state.page = 0; // reset to first page on sort change
      updateQueriedAssets(state);
    },
    setPage: (state, action: PayloadAction<{ page: number }>) => {
      state.page = action.payload.page;
      updateQueriedAssets(state);
    },

    addOpenedAsset: (state, action: PayloadAction<{ assetId: string }>) => {
      const assetId = action.payload.assetId;
      const assetInfo = getAssetInfo(assetId, state)!;
      if (assetInfo && !findOpenedAsset(state, assetId)) {
        state.openedAssets.unshift(assetInfo);
        saveOpenedAssets(state);
      }
    },
    closeOpenedAsset: (state, action: PayloadAction<{ assetId: string }>) => {
      state.openedAssets = state.openedAssets.filter((asset) => asset.id != action.payload.assetId);
      delete state.lastSearchQuery[action.payload.assetId];
      saveOpenedAssets(state);
      saveLastSearchQuery(state);
    },
    modifyOpenedAsset: (state, action: PayloadAction<{ asset: TpAssetInfo }>) => {
      state.openedAssets = state.openedAssets.map((asset) => {
        if (asset.id == action.payload.asset.id) return action.payload.asset;
        return asset;
      });
      state.queriedAssets = state.queriedAssets.map((asset) => {
        if (asset.id == action.payload.asset.id) return action.payload.asset;
        return asset;
      });
      saveOpenedAssets(state);
      updateQueriedAssets(state);
    },

    createAndOpenListAsset: (
      state,
      action: PayloadAction<{
        newAssetId: string;
        parentAssetId: string;
        dataContext: TpDataContext;
        canonicalTypeId: string;
        name: string;
        description: string;
      }>,
    ) => {
      performCreateAndOpenListAsset(
        state,
        action.payload.newAssetId,
        action.payload.parentAssetId,
        action.payload.dataContext,
        action.payload.canonicalTypeId,
        action.payload.name,
        action.payload.description,
      );
    },
    setFetchedAssets: (state, action: PayloadAction<{ assets: TpAssetInfo[]; queryStats: any }>) => {
      const { assets, queryStats } = action.payload;
      state.fetchedAssets = assets;

      const existingAssetIds = new Set(state.queriedAssets.map((asset) => asset.id));
      const newAssets = assets.filter((asset) => !existingAssetIds.has(asset.id));
      state.queriedAssets = [...state.queriedAssets, ...newAssets];
      state.queryStats = queryStats;
      updateQueriedAssets(state);
    },
    addNewAsset: (state, action: PayloadAction<{ asset: TpAssetInfo }>) => {
      const assetExists = state.queriedAssets.find((asset) => asset.id == action.payload.asset.id);
      if (assetExists) return;
      state.queriedAssets = [...state.queriedAssets, action.payload.asset];
      state.queryStats[action.payload.asset.type] = (state.queryStats[action.payload.asset.type] || 0) + 1;
      updateQueriedAssets(state);
    },
    deleteAsset: (state, action: PayloadAction<{ assetId: string }>) => {
      state.openedAssets = state.openedAssets.filter((asset) => asset.id != action.payload.assetId);
      state.queriedAssets = state.queriedAssets.filter((asset) => asset.id != action.payload.assetId);
      delete state.lastSearchQuery[action.payload.assetId];
      saveLastSearchQuery(state);
      saveOpenedAssets(state);
      updateQueriedAssets(state);
    },
    modifyAsset: (state, action: PayloadAction<{ asset: TpAssetInfo }>) => {
      state.queriedAssets = state.queriedAssets.map((asset) => {
        if (asset.id == action.payload.asset.id) return action.payload.asset;
        return asset;
      });
      updateQueriedAssets(state);
    },
    setInitialAssetsState: (state) => {
      state.searchText = '';
      state.typeFilter = '';
      state.sortBy = '';
      state.sortAsc = true;
      state.page = 0;
      state.fetchedAssets = [];
      state.queriedAssets = [];
      state.openedAssets = [];
      state.queriedAssetTypes = [];
      state.queryStats = {};
      state.catalogAsset = null;
      state.lastSearchQuery = {};
      // Clear session storage
      clearOpenedAssets();
      clearLastSearchQuery();
    },
    setCatalogAsset: (state, action: PayloadAction<{ asset: TpAssetInfo }>) => {
      state.catalogAsset = action.payload.asset;
    },
  },
});

export const {
  setSearchText,
  setAssetLastSearchQuery,
  setTypeFilter,
  setSortBy,
  setPage,
  addOpenedAsset,
  closeOpenedAsset,
  modifyOpenedAsset,
  createAndOpenListAsset,
  setFetchedAssets,
  addNewAsset,
  deleteAsset,
  modifyAsset,
  setInitialAssetsState,
  setCatalogAsset,
} = queriedAssetsSlice.actions;

export const selectQueriedAssets = (state: RootState) => state.queriedAssets;

export const selectAllAssetsIncludingSystem = (state: RootState) => state.queriedAssets.queriedAssets;

export const selectNonSystemAssets = (state: RootState) =>
  state.queriedAssets.queriedAssets.filter((asset) => !asset.isSystemAsset);

export const selectCatalogAsset = (state: RootState) => state.queriedAssets.catalogAsset;

export default queriedAssetsSlice.reducer;
